import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, DotsThreeVertical, Sparkle, Printer, House, Clock, Warning, CheckCircle, PencilSimple, ArrowUUpRight, Copy, Envelope, WhatsappLogo, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import CustomerLink from "@/components/CustomerLink";

const TYPES = ["Residential", "Commercial", "Shop", "Office", "Leave & License"];
const DURATIONS = [11, 22, 33, 44, 55];
const MODES = ["Cash", "UPI", "NEFT", "RTGS", "IMPS", "Cheque", "Bank Transfer"];
const STATUSES = ["Draft", "Active", "Expired", "Cancelled"];
const STATUS_TONE = {
  Draft: "bg-slate-100 text-slate-700 border-slate-200",
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Expired: "bg-amber-50 text-amber-700 border-amber-300",
  Cancelled: "bg-red-50 text-red-700 border-red-300",
};

const emptyParty = { name: "", father_name: "", mobile: "", email: "", aadhaar: "", pan: "", occupation: "", address: "", city: "", state: "", pin: "" };
const emptyProperty = { flat_no: "", building: "", floor: "", wing: "", road: "", area: "", landmark: "", city: "", district: "", state: "", pin: "", property_type: "Residential", carpet_area: "", parking: "" };
const emptyPayment = { mode: "", txn_no: "", bank_name: "", account_holder: "", payment_date: "", reference: "", remarks: "" };
const emptyWitness = { name: "", mobile: "", aadhaar: "" };

const EMPTY = {
  priority: "Normal", operator: "", branch: "Main",
  agreement_type: "Residential",
  start_date: "", duration_months: 11, end_date: "",
  owner: { ...emptyParty }, tenant: { ...emptyParty }, property: { ...emptyProperty },
  monthly_rent: 0, security_deposit: 0, deposit_received: false,
  rent_due_day: 5, rent_increase_pct: 0, lock_in_months: 0,
  deposit_payment: { ...emptyPayment },
  witness1: { ...emptyWitness }, witness2: { ...emptyWitness },
  customer_id: "", status: "Draft", notes: "",
};

function addMonths(iso, months) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const y = d.getFullYear();
  const m = d.getMonth() + Number(months);
  const day = Math.min(d.getDate(), 28);
  const nd = new Date(y, m, day);
  const yy = nd.getFullYear();
  const mm = String(nd.getMonth() + 1).padStart(2, "0");
  const dd = String(nd.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function StatCard({ label, value, icon: Icon, tone, testid, onClick, active }) {
  const t = { blue: "bg-blue-50 text-blue-700 border-blue-200", green: "bg-emerald-50 text-emerald-700 border-emerald-200", amber: "bg-amber-50 text-amber-700 border-amber-200", red: "bg-red-50 text-red-700 border-red-200", slate: "bg-slate-100 text-slate-700 border-slate-200" }[tone];
  const activeRing = active ? "ring-2 ring-orange-500 ring-offset-1" : "";
  return (
    <Card
      onClick={onClick}
      data-testid={testid}
      className={`p-5 bg-white border-slate-200 cursor-pointer hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow] duration-200 ${activeRing}`}
    >
      <div className={`w-10 h-10 rounded-md flex items-center justify-center border ${t} mb-3`}><Icon size={20} weight="duotone" /></div>
      <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1">{label}</div>
    </Card>
  );
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

function agreementAlerts(r) {
  const alerts = [];
  const du = daysUntil(r.end_date);
  if (r.status === "Active" && du !== null) {
    if (du >= 0 && du <= 7) alerts.push({ tone: "red", label: `Expiring in ${du}d`, icon: Warning });
    else if (du >= 0 && du <= 30) alerts.push({ tone: "amber", label: `Expiring in ${du}d`, icon: Clock });
    else if (du < 0) alerts.push({ tone: "slate", label: `Expired ${Math.abs(du)}d ago`, icon: Clock });
  }
  if (r.security_deposit > 0 && !r.deposit_received) alerts.push({ tone: "amber", label: "Deposit Pending", icon: Warning });
  return alerts;
}

export default function RentAgreements() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all"); // all | active | expiring_30 | expiring_7 | deposit_pending | completed
  const nav = useNavigate();

  const load = async () => {
    setRows((await api.get("/rent-agreements")).data);
    setStats((await api.get("/rent-agreements-stats/overview")).data);
  };
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  // Auto-calc end_date whenever start_date or duration changes
  const computedEnd = useMemo(() => addMonths(form.start_date, form.duration_months), [form.start_date, form.duration_months]);

  const autoFillFromCustomer = (cid, target = "tenant") => {
    const c = customers.find((x) => x.id === cid);
    if (!c) return;
    setForm((f) => ({
      ...f,
      customer_id: cid,
      [target]: {
        ...f[target],
        name: c.name || "", father_name: c.father_name || "",
        mobile: c.mobile || "", email: c.email || "",
        aadhaar: c.aadhaar || "", pan: c.pan || "",
        occupation: c.occupation || "",
        address: [c.house_no, c.street, c.address].filter(Boolean).join(", "),
        city: c.city || c.taluka || "", state: c.state || "", pin: c.pin || "",
      },
    }));
  };

  const submit = async () => {
    if (!form.start_date) { toast.error("Start date is required"); return; }
    if (!form.owner.name || !form.tenant.name) { toast.error("Owner and Tenant names are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, end_date: form.end_date || computedEnd };
      const { data } = await api.post("/rent-agreements", payload);
      toast.success(`${data.agreement_no} created`);
      setOpen(false); setForm(EMPTY); load();
      if (data.deposit_received) setReceipt(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/rent-agreements/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markDeposit = async (id) => {
    try { const { data } = await api.patch(`/rent-agreements/${id}`, { deposit_received: true }); toast.success("Deposit recorded"); load(); setReceipt(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generateAi = (row) => {
    const tpl = row.agreement_type === "Commercial" ? "rent_commercial"
      : row.agreement_type === "Shop" ? "rent_shop"
      : row.agreement_type === "Office" ? "rent_office"
      : row.agreement_type === "Leave & License" ? "rent_residential"
      : "rent_residential";
    const params = new URLSearchParams();
    params.set("template", tpl);
    if (row.customer_id) params.set("customer", row.customer_id);
    params.set("ra", row.id);
    nav(`/ai-drafts?${params.toString()}`);
  };

  // AI quick-action shortcuts — jump to /ai-drafts with a preset template
  const aiQuick = (row, templateId) => {
    const params = new URLSearchParams();
    params.set("template", templateId);
    if (row.customer_id) params.set("customer", row.customer_id);
    params.set("ra", row.id);
    nav(`/ai-drafts?${params.toString()}`);
  };

  // Clone the current agreement into the New Agreement dialog with a fresh start date one day after old end.
  const renewAgreement = (row) => {
    setForm({
      ...EMPTY,
      priority: row.priority || "Normal",
      operator: row.operator || "",
      branch: row.branch || "Main",
      agreement_type: row.agreement_type || "Residential",
      start_date: "",
      duration_months: row.duration_months || 11,
      owner: { ...emptyParty, ...(row.owner || {}) },
      tenant: { ...emptyParty, ...(row.tenant || {}) },
      property: { ...emptyProperty, ...(row.property || {}) },
      monthly_rent: row.monthly_rent || 0,
      security_deposit: row.security_deposit || 0,
      deposit_received: false,
      rent_due_day: row.rent_due_day || 5,
      rent_increase_pct: row.rent_increase_pct || 0,
      lock_in_months: row.lock_in_months || 0,
      customer_id: row.customer_id || "",
      status: "Draft",
      notes: `Renewal of ${row.agreement_no}. ` + (row.notes || ""),
    });
    setDetail(null);
    setOpen(true);
    toast.success(`Renewing ${row.agreement_no} — set the new start date`);
  };

  // Filtered rows for the clickable summary cards
  const filteredRows = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => {
      const du = daysUntil(r.end_date);
      if (filter === "active") return r.status === "Active";
      if (filter === "expiring_30") return r.status === "Active" && du !== null && du >= 0 && du <= 30;
      if (filter === "expiring_7") return r.status === "Active" && du !== null && du >= 0 && du <= 7;
      if (filter === "deposit_pending") return r.security_deposit > 0 && !r.deposit_received;
      if (filter === "completed") return r.status === "Expired" || r.status === "Cancelled";
      return true;
    });
  }, [rows, filter]);

  const FILTER_LABEL = {
    all: "All Agreements",
    active: "Active",
    expiring_30: "Expiring in 30 days",
    expiring_7: "Expiring in 7 days",
    deposit_pending: "Deposit Pending",
    completed: "Completed / Cancelled",
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2 flex items-center gap-2"><House size={14} weight="duotone" /> Property</div>
          <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Rent Agreements</h1>
          <p className="text-sm text-slate-600 mt-2">Residential · Commercial · Shop · Office · Leave & License — with AI-drafted agreements and deposit receipts.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-ra-btn" className="bg-orange-600 hover:bg-orange-700 text-white h-11 gap-2 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200">
              <Plus size={18} weight="bold" /> New Rent Agreement
            </Button>
          </DialogTrigger>

          <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>New Rent Agreement</DialogTitle>
              <DialogDescription className="sr-only">Create a new Leave & Licence / rent agreement with owner, tenant, property, deposit and witness details.</DialogDescription>
            </DialogHeader>

            <Section>Application</Section>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <F label="Agreement Type *">
                <Select value={form.agreement_type} onValueChange={(v) => setForm({ ...form, agreement_type: v })}>
                  <SelectTrigger data-testid="ra-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Priority">
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Normal", "Urgent", "Emergency"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Operator"><Input value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} /></F>
              <F label="Branch"><Input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} /></F>
            </div>

            <Section>Duration</Section>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <F label="Start Date *"><Input data-testid="ra-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></F>
              <F label="Duration">
                <div className="flex flex-wrap gap-1.5">
                  {DURATIONS.map((d) => (
                    <button key={d} type="button" onClick={() => setForm({ ...form, duration_months: d })}
                      className={`px-3 py-1.5 rounded-full border text-sm transition-all ${form.duration_months === d ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>
                      {d} mo
                    </button>
                  ))}
                  <Input type="number" min="1" placeholder="Custom" value={DURATIONS.includes(Number(form.duration_months)) ? "" : form.duration_months} onChange={(e) => setForm({ ...form, duration_months: Number(e.target.value) || 0 })} className="w-24 h-8 text-xs" />
                </div>
              </F>
              <F label="End Date (auto)">
                <Input data-testid="ra-end-input" type="date" value={form.end_date || computedEnd} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </F>
            </div>

            <Section>Owner Details</Section>
            <PartyForm party={form.owner} onChange={(p) => setForm({ ...form, owner: p })} testidPrefix="ra-owner" />

            <Section>Tenant Details</Section>
            <div className="mb-2">
              <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Auto-fill from existing customer</Label>
              <Select value={form.customer_id || undefined} onValueChange={(v) => autoFillFromCustomer(v, "tenant")}>
                <SelectTrigger data-testid="ra-customer-select"><SelectValue placeholder="Pick a customer to auto-fill tenant" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {customers.slice(0, 100).map((c) => <SelectItem key={c.id} value={c.id}>{c.customer_no} · {c.name} · {c.mobile}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <PartyForm party={form.tenant} onChange={(p) => setForm({ ...form, tenant: p })} withOccupation testidPrefix="ra-tenant" />

            <Section>Property Details</Section>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <F label="Flat / Shop No."><Input value={form.property.flat_no} onChange={(e) => setForm({ ...form, property: { ...form.property, flat_no: e.target.value } })} /></F>
              <F label="Building"><Input value={form.property.building} onChange={(e) => setForm({ ...form, property: { ...form.property, building: e.target.value } })} /></F>
              <F label="Floor"><Input value={form.property.floor} onChange={(e) => setForm({ ...form, property: { ...form.property, floor: e.target.value } })} /></F>
              <F label="Wing"><Input value={form.property.wing} onChange={(e) => setForm({ ...form, property: { ...form.property, wing: e.target.value } })} /></F>
              <F label="Road / Street"><Input value={form.property.road} onChange={(e) => setForm({ ...form, property: { ...form.property, road: e.target.value } })} /></F>
              <F label="Area"><Input value={form.property.area} onChange={(e) => setForm({ ...form, property: { ...form.property, area: e.target.value } })} /></F>
              <F label="Landmark"><Input value={form.property.landmark} onChange={(e) => setForm({ ...form, property: { ...form.property, landmark: e.target.value } })} /></F>
              <F label="City"><Input value={form.property.city} onChange={(e) => setForm({ ...form, property: { ...form.property, city: e.target.value } })} /></F>
              <F label="District"><Input value={form.property.district} onChange={(e) => setForm({ ...form, property: { ...form.property, district: e.target.value } })} /></F>
              <F label="State"><Input value={form.property.state} onChange={(e) => setForm({ ...form, property: { ...form.property, state: e.target.value } })} /></F>
              <F label="PIN"><Input value={form.property.pin} onChange={(e) => setForm({ ...form, property: { ...form.property, pin: e.target.value } })} /></F>
              <F label="Property Type"><Input value={form.property.property_type} onChange={(e) => setForm({ ...form, property: { ...form.property, property_type: e.target.value } })} /></F>
              <F label="Carpet Area (sq. ft.)"><Input value={form.property.carpet_area} onChange={(e) => setForm({ ...form, property: { ...form.property, carpet_area: e.target.value } })} /></F>
              <F label="Parking Details"><Input value={form.property.parking} onChange={(e) => setForm({ ...form, property: { ...form.property, parking: e.target.value } })} /></F>
            </div>

            <Section>Rent & Deposit</Section>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <F label="Monthly Rent (₹)"><Input data-testid="ra-rent-input" type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: Number(e.target.value) })} /></F>
              <F label="Security Deposit (₹)"><Input data-testid="ra-deposit-input" type="number" value={form.security_deposit} onChange={(e) => setForm({ ...form, security_deposit: Number(e.target.value) })} /></F>
              <F label="Rent Due Day"><Input type="number" min="1" max="31" value={form.rent_due_day} onChange={(e) => setForm({ ...form, rent_due_day: Number(e.target.value) })} /></F>
              <F label="Annual Increase %"><Input type="number" value={form.rent_increase_pct} onChange={(e) => setForm({ ...form, rent_increase_pct: Number(e.target.value) })} /></F>
              <F label="Lock-in (months)"><Input type="number" value={form.lock_in_months} onChange={(e) => setForm({ ...form, lock_in_months: Number(e.target.value) })} /></F>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.deposit_received} onChange={(e) => setForm({ ...form, deposit_received: e.target.checked })} data-testid="ra-deposit-received" className="w-4 h-4" />
                  <span className="text-sm text-slate-700">Deposit received</span>
                </label>
              </div>
            </div>

            {form.deposit_received && (
              <>
                <Section>Deposit Payment</Section>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <F label="Mode">
                    <Select value={form.deposit_payment.mode} onValueChange={(v) => setForm({ ...form, deposit_payment: { ...form.deposit_payment, mode: v } })}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Txn / UTR No."><Input value={form.deposit_payment.txn_no} onChange={(e) => setForm({ ...form, deposit_payment: { ...form.deposit_payment, txn_no: e.target.value } })} /></F>
                  <F label="Bank"><Input value={form.deposit_payment.bank_name} onChange={(e) => setForm({ ...form, deposit_payment: { ...form.deposit_payment, bank_name: e.target.value } })} /></F>
                  <F label="Account Holder"><Input value={form.deposit_payment.account_holder} onChange={(e) => setForm({ ...form, deposit_payment: { ...form.deposit_payment, account_holder: e.target.value } })} /></F>
                  <F label="Payment Date"><Input type="date" value={form.deposit_payment.payment_date} onChange={(e) => setForm({ ...form, deposit_payment: { ...form.deposit_payment, payment_date: e.target.value } })} /></F>
                  <F label="Reference"><Input value={form.deposit_payment.reference} onChange={(e) => setForm({ ...form, deposit_payment: { ...form.deposit_payment, reference: e.target.value } })} /></F>
                  <div className="md:col-span-3"><F label="Remarks"><Input value={form.deposit_payment.remarks} onChange={(e) => setForm({ ...form, deposit_payment: { ...form.deposit_payment, remarks: e.target.value } })} /></F></div>
                </div>
              </>
            )}

            <Section>Witnesses</Section>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2 font-semibold">Witness 1</div>
                <WitnessForm w={form.witness1} onChange={(w) => setForm({ ...form, witness1: w })} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2 font-semibold">Witness 2</div>
                <WitnessForm w={form.witness2} onChange={(w) => setForm({ ...form, witness2: w })} />
              </div>
            </div>

            <Section>Notes</Section>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => { setOpen(false); setForm(EMPTY); }}>Cancel</Button>
              <Button data-testid="ra-save-btn" disabled={saving} onClick={submit} className="bg-orange-600 hover:bg-orange-700 text-white">
                {saving ? "Saving…" : "Create Agreement"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <StatCard label="Active" value={stats.active || 0} icon={CheckCircle} tone="green" testid="ra-stat-active" onClick={() => setFilter("active")} active={filter === "active"} />
        <StatCard label="Expiring 30d" value={stats.expiring_30_days || 0} icon={Clock} tone="amber" testid="ra-stat-30" onClick={() => setFilter("expiring_30")} active={filter === "expiring_30"} />
        <StatCard label="Expiring 7d" value={stats.expiring_7_days || 0} icon={Warning} tone="red" testid="ra-stat-7" onClick={() => setFilter("expiring_7")} active={filter === "expiring_7"} />
        <StatCard label="Deposit Pending" value={stats.deposit_pending || 0} icon={Warning} tone="amber" testid="ra-stat-dep" onClick={() => setFilter("deposit_pending")} active={filter === "deposit_pending"} />
        <StatCard label="Completed" value={stats.completed || 0} icon={CheckCircle} tone="slate" testid="ra-stat-completed" onClick={() => setFilter("completed")} active={filter === "completed"} />
        <StatCard label="Total" value={stats.total || 0} icon={House} tone="blue" testid="ra-stat-total" onClick={() => setFilter("all")} active={filter === "all"} />
      </div>

      <div className="mb-4 flex items-center gap-2 text-sm text-slate-600" data-testid="ra-filter-bar">
        Showing: <Badge variant="outline" className="bg-white">{FILTER_LABEL[filter]} · {filteredRows.length}</Badge>
        {filter !== "all" && (
          <button data-testid="ra-filter-clear" onClick={() => setFilter("all")} className="text-xs text-orange-600 hover:text-orange-700 underline underline-offset-2">Clear filter</button>
        )}
      </div>

      <Card className="bg-white border-slate-200 overflow-hidden" data-testid="ra-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Agreement #</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Type</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Owner → Tenant</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Property</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Period</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">Rent</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-16">{rows.length === 0 ? "No rent agreements yet." : "No agreements match this filter."}</TableCell></TableRow>}
            {filteredRows.map((r) => {
              const alerts = agreementAlerts(r);
              return (
              <TableRow key={r.id} className="hover:bg-slate-50">
                <TableCell>
                  <button
                    type="button"
                    onClick={() => setDetail(r)}
                    data-testid={`ra-open-${r.id}`}
                    className="font-mono text-xs text-orange-700 hover:text-orange-800 hover:underline underline-offset-2"
                    title="Open Agreement 360° profile"
                  >
                    {r.agreement_no}
                  </button>
                  {r.deposit_receipt_no && <div className="text-[10px] font-mono text-emerald-700">{r.deposit_receipt_no}</div>}
                  {alerts.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {alerts.map((a) => (
                        <Badge key={a.label} variant="outline" className={`text-[9px] uppercase tracking-wider gap-1 py-0 px-1.5
                          ${a.tone === "red" ? "border-red-300 text-red-700 bg-red-50"
                            : a.tone === "amber" ? "border-amber-300 text-amber-700 bg-amber-50"
                            : "border-slate-300 text-slate-700 bg-slate-50"}`}>
                          <a.icon size={10} weight="fill" /> {a.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{r.agreement_type}</Badge></TableCell>
                <TableCell>
                  <div className="text-sm text-slate-900">
                    <span className="text-slate-700">{r.owner?.name || "—"}</span>
                    <span className="mx-1 text-slate-400">→</span>
                    {r.customer_id
                      ? <CustomerLink id={r.customer_id} name={r.tenant?.name || r.customer_name} />
                      : <button type="button" onClick={() => setDetail(r)} className="text-slate-900 hover:text-orange-600 hover:underline underline-offset-2">{r.tenant?.name || "—"}</button>}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-slate-600">{[r.property?.flat_no, r.property?.building, r.property?.area, r.property?.city].filter(Boolean).join(", ") || "—"}</TableCell>
                <TableCell className="text-xs">{r.start_date}<br/><span className="text-slate-500">to {r.end_date}</span></TableCell>
                <TableCell className="text-right font-semibold">{inr(r.monthly_rent)}</TableCell>
                <TableCell><Badge variant="outline" className={`${STATUS_TONE[r.status]} font-medium text-[10px] uppercase tracking-wider`}>{r.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`ra-menu-${r.id}`}><DotsThreeVertical size={18} /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white" align="end">
                      <DropdownMenuItem onClick={() => setDetail(r)}>View 360° Profile</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => generateAi(r)} data-testid={`ra-ai-${r.id}`}><Sparkle size={14} className="mr-2" /> Generate AI Draft</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => renewAgreement(r)} data-testid={`ra-renew-${r.id}`}><ArrowUUpRight size={14} className="mr-2" /> Renew Agreement</DropdownMenuItem>
                      {!r.deposit_received && <DropdownMenuItem onClick={() => markDeposit(r.id)}>Mark Deposit Received</DropdownMenuItem>}
                      {r.deposit_receipt_no && <DropdownMenuItem onClick={() => setReceipt(r)}>View Deposit Receipt</DropdownMenuItem>}
                      {STATUSES.map((s) => <DropdownMenuItem key={s} onClick={() => changeStatus(r.id, s)} disabled={r.status === s}>Mark {s}</DropdownMenuItem>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Agreement 360° Profile */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }} className="flex items-center gap-3 flex-wrap">
              <span>{detail?.agreement_no}</span>
              {detail && <Badge variant="outline" className={`${STATUS_TONE[detail.status]} text-[10px] uppercase tracking-wider`}>{detail.status}</Badge>}
              {detail && agreementAlerts(detail).map((a) => (
                <Badge key={a.label} variant="outline" className={`text-[10px] uppercase tracking-wider gap-1
                  ${a.tone === "red" ? "border-red-300 text-red-700 bg-red-50"
                    : a.tone === "amber" ? "border-amber-300 text-amber-700 bg-amber-50"
                    : "border-slate-300 text-slate-700 bg-slate-50"}`}>
                  <a.icon size={10} weight="fill" /> {a.label}
                </Badge>
              ))}
            </DialogTitle>
            <DialogDescription className="sr-only">Complete 360° profile of the rent agreement including owner, tenant, property, deposit and AI-assisted draft shortcuts.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-5 py-2" data-testid="ra-detail-360">
              <div className="grid grid-cols-3 gap-3">
                <MiniInfo label="Type" value={detail.agreement_type} />
                <MiniInfo label="Priority" value={detail.priority} />
                <MiniInfo label="Branch" value={detail.branch} />
                <MiniInfo label="Start" value={detail.start_date} />
                <MiniInfo label="End" value={detail.end_date} />
                <MiniInfo label="Duration" value={`${detail.duration_months} months`} />
                <MiniInfo label="Monthly Rent" value={inr(detail.monthly_rent)} />
                <MiniInfo label="Security Deposit" value={inr(detail.security_deposit)} />
                <MiniInfo label="Rent Due Day" value={`Day ${detail.rent_due_day || 5}`} />
              </div>

              <ProfileBlock title="Owner (Licensor)">
                <div className="text-sm text-slate-900 font-medium">{detail.owner?.name || "—"}</div>
                <div className="text-xs text-slate-500 mt-0.5">{[detail.owner?.mobile, detail.owner?.email].filter(Boolean).join(" · ")}</div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">{[detail.owner?.aadhaar && `Aadhaar ${detail.owner.aadhaar}`, detail.owner?.pan && `PAN ${detail.owner.pan}`].filter(Boolean).join(" · ")}</div>
                <div className="text-xs text-slate-500 mt-1">{[detail.owner?.address, detail.owner?.city, detail.owner?.state, detail.owner?.pin].filter(Boolean).join(", ")}</div>
              </ProfileBlock>

              <ProfileBlock title="Tenant (Licensee)">
                <div className="text-sm text-slate-900 font-medium">
                  {detail.customer_id
                    ? <CustomerLink id={detail.customer_id} name={detail.tenant?.name || detail.customer_name} />
                    : (detail.tenant?.name || "—")}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{[detail.tenant?.mobile, detail.tenant?.email].filter(Boolean).join(" · ")}</div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">{[detail.tenant?.aadhaar && `Aadhaar ${detail.tenant.aadhaar}`, detail.tenant?.pan && `PAN ${detail.tenant.pan}`].filter(Boolean).join(" · ")}</div>
                <div className="text-xs text-slate-500 mt-1">{[detail.tenant?.address, detail.tenant?.city, detail.tenant?.state, detail.tenant?.pin].filter(Boolean).join(", ")}</div>
              </ProfileBlock>

              <ProfileBlock title="Property">
                <div className="text-sm text-slate-900">{[detail.property?.flat_no, detail.property?.building, detail.property?.floor, detail.property?.wing].filter(Boolean).join(", ") || "—"}</div>
                <div className="text-xs text-slate-500 mt-0.5">{[detail.property?.road, detail.property?.area, detail.property?.landmark, detail.property?.city, detail.property?.district, detail.property?.state, detail.property?.pin].filter(Boolean).join(", ")}</div>
                <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-3">
                  {detail.property?.property_type && <span>Type: <b>{detail.property.property_type}</b></span>}
                  {detail.property?.carpet_area && <span>Carpet: <b>{detail.property.carpet_area}</b> sq. ft.</span>}
                  {detail.property?.parking && <span>Parking: <b>{detail.property.parking}</b></span>}
                </div>
              </ProfileBlock>

              <ProfileBlock title="Deposit">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500 text-[10px] uppercase tracking-widest">Amount</span><div className="font-semibold">{inr(detail.security_deposit)}</div></div>
                  <div><span className="text-slate-500 text-[10px] uppercase tracking-widest">Status</span><div>{detail.deposit_received ? <span className="text-emerald-700 font-medium">Received</span> : <span className="text-amber-700 font-medium">Pending</span>}</div></div>
                  {detail.deposit_receipt_no && <div><span className="text-slate-500 text-[10px] uppercase tracking-widest">Receipt #</span><div className="font-mono">{detail.deposit_receipt_no}</div></div>}
                  {detail.deposit_payment?.mode && <div><span className="text-slate-500 text-[10px] uppercase tracking-widest">Mode</span><div>{detail.deposit_payment.mode}</div></div>}
                  {detail.deposit_payment?.txn_no && <div><span className="text-slate-500 text-[10px] uppercase tracking-widest">Txn / UTR</span><div className="font-mono text-xs">{detail.deposit_payment.txn_no}</div></div>}
                  {detail.deposit_payment?.payment_date && <div><span className="text-slate-500 text-[10px] uppercase tracking-widest">Paid On</span><div>{detail.deposit_payment.payment_date}</div></div>}
                </div>
              </ProfileBlock>

              {/* AI Quick Actions */}
              <div className="rounded-md border border-purple-200 bg-purple-50/40 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-purple-700 mb-2 flex items-center gap-2"><Sparkle size={12} weight="fill" /> AI Assistant · One-click drafts</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => generateAi(detail)} data-testid="ra-ai-renewal" className="border-purple-300 text-purple-700 hover:bg-purple-100 gap-1 h-8"><ArrowUUpRight size={12} /> Renewal Draft</Button>
                  <Button size="sm" variant="outline" onClick={() => aiQuick(detail, "legal_undertaking")} className="border-purple-300 text-purple-700 hover:bg-purple-100 gap-1 h-8"><FileText size={12} /> Rent Increase Letter</Button>
                  <Button size="sm" variant="outline" onClick={() => aiQuick(detail, "noc_rent")} data-testid="ra-ai-noc" className="border-purple-300 text-purple-700 hover:bg-purple-100 gap-1 h-8"><FileText size={12} /> Draft NOC</Button>
                  <Button size="sm" variant="outline" onClick={() => aiQuick(detail, "biz_receipt")} className="border-purple-300 text-purple-700 hover:bg-purple-100 gap-1 h-8"><FileText size={12} /> Deposit Receipt Draft</Button>
                  <Button size="sm" variant="outline" onClick={() => aiQuick(detail, "legal_declaration")} className="border-purple-300 text-purple-700 hover:bg-purple-100 gap-1 h-8"><FileText size={12} /> Summary</Button>
                </div>
              </div>

              {/* Communication + Actions */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
                <Button onClick={() => renewAgreement(detail)} data-testid="ra-detail-renew" className="bg-orange-600 hover:bg-orange-700 text-white gap-2"><ArrowUUpRight size={14} /> Renew Agreement</Button>
                {!detail.deposit_received && <Button variant="outline" onClick={() => markDeposit(detail.id)}>Mark Deposit Received</Button>}
                {detail.deposit_receipt_no && <Button variant="outline" onClick={() => setReceipt(detail)} className="gap-2"><Printer size={14} /> Deposit Receipt</Button>}
                {detail.tenant?.mobile && (
                  <a
                    href={`https://wa.me/91${(detail.tenant.mobile || "").replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Namaste ${detail.tenant.name || ""}, this is a reminder for your rent agreement ${detail.agreement_no} due on day ${detail.rent_due_day || 5} each month. — DigitalSetu`)}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm"
                  ><WhatsappLogo size={14} weight="fill" /> WhatsApp Tenant</a>
                )}
                {detail.tenant?.email && (
                  <a
                    href={`mailto:${detail.tenant.email}?subject=${encodeURIComponent(`Agreement ${detail.agreement_no}`)}`}
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm"
                  ><Envelope size={14} /> Email Tenant</a>
                )}
                <Button variant="outline" onClick={() => window.print()} className="gap-2"><Printer size={14} /> Print</Button>
                {detail.status !== "Cancelled" && <Button variant="outline" onClick={() => changeStatus(detail.id, "Cancelled")} className="border-red-300 text-red-700 hover:bg-red-50">Cancel / Archive</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deposit Receipt */}
      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Deposit Receipt</DialogTitle></DialogHeader>
          {receipt && (
            <div className="print-area py-2 text-sm">
              <div className="text-center border-b border-dashed border-slate-300 pb-3 mb-3">
                <div className="font-bold text-lg" style={{ fontFamily: "var(--font-heading)" }}>PRIYANKA ENTERPRISE · ERP</div>
                <div className="text-xs text-slate-500">Security Deposit Receipt</div>
              </div>
              <RRow label="Receipt #" value={receipt.deposit_receipt_no} />
              <RRow label="Agreement #" value={receipt.agreement_no} />
              <RRow label="Type" value={receipt.agreement_type} />
              <RRow label="Date" value={receipt.deposit_payment?.payment_date || new Date().toLocaleDateString()} />
              <div className="border-t border-dashed border-slate-300 my-2" />
              <RRow label="Received From" value={receipt.tenant?.name} />
              <RRow label="Mobile" value={receipt.tenant?.mobile} />
              <RRow label="Property" value={[receipt.property?.flat_no, receipt.property?.building, receipt.property?.city].filter(Boolean).join(", ")} />
              <div className="border-t border-dashed border-slate-300 my-2" />
              <RRow label="Mode" value={receipt.deposit_payment?.mode} />
              <RRow label="Txn / UTR" value={receipt.deposit_payment?.txn_no} />
              <RRow label="Bank" value={receipt.deposit_payment?.bank_name} />
              <div className="border-t border-dashed border-slate-300 my-3" />
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold">DEPOSIT AMOUNT</span>
                <span className="text-2xl font-bold text-orange-600" style={{ fontFamily: "var(--font-heading)" }}>{inr(receipt.security_deposit)}</span>
              </div>
              <div className="text-center text-[10px] text-slate-400 mt-4">Refundable as per agreement terms · Thank you</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceipt(null)}>Close</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" onClick={() => window.print()}><Printer size={14} /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">{label}</Label>{children}</div>;
}
function Section({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-orange-600 mt-5 mb-3 pb-1 border-b border-slate-200">{children}</div>;
}
function MiniInfo({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div><div className="text-sm text-slate-900">{value || "—"}</div></div>;
}
function ProfileBlock({ title, children }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-orange-600 mb-2">{title}</div>
      {children}
    </div>
  );
}
function RRow({ label, value }) {
  return <div className="flex justify-between text-xs py-0.5"><span className="text-slate-600">{label}</span><span className="font-medium text-slate-900">{value || "—"}</span></div>;
}
function PartyForm({ party, onChange, withOccupation, testidPrefix }) {
  const upd = (k, v) => onChange({ ...party, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <F label="Name *"><Input data-testid={`${testidPrefix}-name`} value={party.name} onChange={(e) => upd("name", e.target.value)} /></F>
      <F label="Father / Husband"><Input value={party.father_name} onChange={(e) => upd("father_name", e.target.value)} /></F>
      <F label="Mobile"><Input value={party.mobile} onChange={(e) => upd("mobile", e.target.value)} /></F>
      <F label="Email"><Input type="email" value={party.email} onChange={(e) => upd("email", e.target.value)} /></F>
      <F label="Aadhaar"><Input value={party.aadhaar} onChange={(e) => upd("aadhaar", e.target.value)} /></F>
      <F label="PAN"><Input value={party.pan} onChange={(e) => upd("pan", e.target.value)} /></F>
      {withOccupation && <F label="Occupation"><Input value={party.occupation} onChange={(e) => upd("occupation", e.target.value)} /></F>}
      <div className={withOccupation ? "md:col-span-3" : "md:col-span-2"}><F label="Address"><Input value={party.address} onChange={(e) => upd("address", e.target.value)} /></F></div>
      <F label="City"><Input value={party.city} onChange={(e) => upd("city", e.target.value)} /></F>
      <F label="State"><Input value={party.state} onChange={(e) => upd("state", e.target.value)} /></F>
      <F label="PIN"><Input value={party.pin} onChange={(e) => upd("pin", e.target.value)} /></F>
    </div>
  );
}
function WitnessForm({ w, onChange }) {
  const upd = (k, v) => onChange({ ...w, [k]: v });
  return (
    <div className="grid grid-cols-1 gap-3">
      <F label="Name"><Input value={w.name} onChange={(e) => upd("name", e.target.value)} /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Mobile"><Input value={w.mobile} onChange={(e) => upd("mobile", e.target.value)} /></F>
        <F label="Aadhaar"><Input value={w.aadhaar} onChange={(e) => upd("aadhaar", e.target.value)} /></F>
      </div>
    </div>
  );
}
